import { useEffect, useState } from "react";
import { cn } from "@/lib/cn";
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/opacity.css';

const DB_NAME = "image-cache";
const STORE_NAME = "images";
const DB_VERSION = 1;

// Module-level cached open() promise. Without this, every image opens its own
// connection — with 20 images that's 20 separate open ceremonies and was the
// single largest source of perceived lag.
let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = (e) => {
        const db = (e.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: "url" });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => {
        // Reset so a later caller can retry instead of a permanent failure.
        dbPromise = null;
        reject(request.error);
      };
    });
  }
  return dbPromise;
}

async function getFromCache(url: string): Promise<Blob | null> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const store = tx.objectStore(STORE_NAME);
    const request = store.get(url);
    request.onsuccess = () => resolve(request.result?.blob ?? null);
    request.onerror = () => reject(request.error);
  });
}

async function saveToCache(url: string, blob: Blob): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const request = store.put({ url, blob });
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export function useImageCache(url: string) {
  const [src, setSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!url) return;

    let objectUrl: string | null = null;
    let canceled = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        let blob = await getFromCache(url);
        if (!blob) {
          const response = await fetch(url);
          if (!response.ok) {
            throw new Error(`Failed to fetch image: ${response.status}`);
          }
          blob = await response.blob();
          // Fire-and-forget. Awaiting the write would delay the image render
          // for no user-visible benefit; failures are non-fatal (next visit
          // just refetches from network).
          void saveToCache(url, blob).catch(() => { });
        }

        if (canceled) return;

        // Store Blob directly in IndexedDB (native support) and hand the
        // browser an object URL instead of a 2-3 MB base64 string. Avoids
        // the main-thread base64 decode that caused the visible jank.
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      } catch (err) {
        if (canceled) return;
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        if (!canceled) setLoading(false);
      }
    }

    load();

    return () => {
      canceled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);

  return { src, loading, error };
}

interface CachedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  fallback?: React.ReactNode;
}

export default function Image({
  src,
  fallback,
  alt,
  className,
  // Default to lazy loading + async decoding so list/grid cards don't block
  // the main thread decoding many covers at once (the navigation jank).
  // Above-the-fold images can still opt out with loading="eager".
  loading = "lazy",
  decoding = "async",
  ...props
}: CachedImageProps) {
  // const { src: url, loading, error } = useImageCache(src);

  // if (error) {
  //   return (
  //     <img
  //       src="/broken-image.png"
  //       className={className}
  //       {...props}
  //     />
  //   );
  // }

  // if (loading || !src) {
  //   return fallback ? (
  //     <>{fallback}</>
  //   ) : (
  //     <div
  //       className={cn("animate-pulse bg-default-100", className)}
  //       style={props.style}
  //     />
  //   );
  // }

  return (
    <LazyLoadImage
      src={src}
      {...props}
      alt={alt} 
      className={`${className}`}
      effect="opacity"
      decoding="async"
      loading="lazy"
    />
  )

  // return <img src={src} alt={alt} className={className} {...props} />;
}